<template>
  <div class="page-container">
    <div class="toolbar">
      <el-button type="primary" @click="openModal">新建集成</el-button>
      <el-button @click="testConnection">测试连接</el-button>
    </div>

    <div class="stats-row">
      <div class="stat-card">
        <div class="stat-icon">
          <el-icon><Connection /></el-icon>
        </div>
        <div class="stat-info">
          <div class="stat-value">{{ integrations.length }}</div>
          <div class="stat-label">集成总数</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon success">
          <el-icon><CircleCheck /></el-icon>
        </div>
        <div class="stat-info">
          <div class="stat-value">{{ enabledCount }}</div>
          <div class="stat-label">运行中</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon warning">
          <el-icon><Clock /></el-icon>
        </div>
        <div class="stat-info">
          <div class="stat-value">{{ lastSyncTime }}</div>
          <div class="stat-label">最近同步</div>
        </div>
      </div>
    </div>

    <el-table stripe :data="integrations" border>
      <el-table-column prop="integrationName" label="集成名称" />
      <el-table-column prop="integrationType" label="集成类型">
        <template #default="scope">
          <el-tag :type="scope.row.integrationType === 'API' ? 'primary' : 'success'">
            {{ scope.row.integrationType }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="endpoint" label="连接地址" />
      <el-table-column prop="lastSync" label="最近同步" />
      <el-table-column prop="syncCount" label="同步次数" />
      <el-table-column prop="status" label="状态">
        <template #default="scope">
          <el-tag :type="scope.row.status === '启用' ? 'success' : 'info'">
            {{ scope.row.status }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="操作">
        <template #default="scope">
          <el-button size="small" @click="editIntegration(scope.row)">编辑</el-button>
          <el-button size="small" @click="syncIntegration(scope.row)">同步</el-button>
          <el-button size="small" type="danger" @click="deleteIntegration(scope.row.id)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog title="集成配置" v-model="dialogVisible" width="600px">
      <el-form :model="formData" label-width="120px">
        <el-form-item label="集成名称" required>
          <el-input v-model="formData.integrationName" placeholder="请输入集成名称" />
        </el-form-item>
        <el-form-item label="集成类型" required>
          <el-select v-model="formData.integrationType">
            <el-option label="REST API" value="API" />
            <el-option label="MQTT" value="MQTT" />
            <el-option label="WebSocket" value="WebSocket" />
            <el-option label="数据库同步" value="Database" />
          </el-select>
        </el-form-item>
        <el-form-item label="连接地址" required>
          <el-input v-model="formData.endpoint" placeholder="请输入连接地址" />
        </el-form-item>
        <el-form-item label="认证方式">
          <el-select v-model="formData.authType">
            <el-option label="无认证" value="none" />
            <el-option label="Basic Auth" value="basic" />
            <el-option label="API Key" value="apikey" />
            <el-option label="Bearer Token" value="bearer" />
          </el-select>
        </el-form-item>
        <el-form-item label="用户名" v-if="formData.authType === 'basic'">
          <el-input v-model="formData.username" placeholder="请输入用户名" />
        </el-form-item>
        <el-form-item label="密码" v-if="formData.authType === 'basic'">
          <el-input v-model="formData.password" type="password" placeholder="请输入密码" />
        </el-form-item>
        <el-form-item label="API Key" v-if="formData.authType === 'apikey'">
          <el-input v-model="formData.apiKey" placeholder="请输入API Key" />
        </el-form-item>
        <el-form-item label="同步间隔(分钟)">
          <el-input-number v-model="formData.syncInterval" :min="1" :max="1440" />
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="formData.status">
            <el-option label="启用" value="启用" />
            <el-option label="禁用" value="禁用" />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSave">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import { Connection, CircleCheck, Clock } from '@element-plus/icons-vue'

const integrations = ref([
  { id: 1, integrationName: 'ERP系统集成', integrationType: 'API', endpoint: 'http://erp.example.com/api', authType: 'bearer', syncInterval: 60, status: '启用', lastSync: '2024-01-15 10:30:00', syncCount: 128 },
  { id: 2, integrationName: '物联网数据同步', integrationType: 'MQTT', endpoint: 'mqtt://iot.example.com:1883', authType: 'basic', syncInterval: 5, status: '启用', lastSync: '2024-01-15 10:45:00', syncCount: 3240 },
  { id: 3, integrationName: '仓储管理系统', integrationType: 'API', endpoint: 'http://wms.example.com/api', authType: 'apikey', syncInterval: 30, status: '禁用', lastSync: '2024-01-14 16:00:00', syncCount: 85 },
  { id: 4, integrationName: '财务系统对接', integrationType: 'API', endpoint: 'http://finance.example.com/api', authType: 'basic', syncInterval: 120, status: '启用', lastSync: '2024-01-15 08:00:00', syncCount: 42 },
  { id: 5, integrationName: '监控数据采集', integrationType: 'WebSocket', endpoint: 'ws://monitor.example.com/ws', authType: 'none', syncInterval: 1, status: '启用', lastSync: '2024-01-15 10:45:30', syncCount: 15600 }
])

const dialogVisible = ref(false)
const formData = reactive({
  id: null,
  integrationName: '',
  integrationType: 'API',
  endpoint: '',
  authType: 'none',
  username: '',
  password: '',
  apiKey: '',
  syncInterval: 60,
  status: '启用'
})

const enabledCount = computed(() => integrations.value.filter(i => i.status === '启用').length)
const lastSyncTime = computed(() => {
  const enabled = integrations.value.filter(i => i.status === '启用')
  if (enabled.length === 0) return '-'
  return enabled.reduce((latest, i) => i.lastSync > latest ? i.lastSync : latest, '')
})

onMounted(() => {})

const openModal = () => {
  Object.assign(formData, { id: null, integrationName: '', integrationType: 'API', endpoint: '', authType: 'none', username: '', password: '', apiKey: '', syncInterval: 60, status: '启用' })
  dialogVisible.value = true
}

const editIntegration = (row) => {
  Object.assign(formData, row)
  dialogVisible.value = true
}

const handleSave = () => {
  if (!formData.integrationName) {
    ElMessage.warning('请输入集成名称')
    return
  }
  if (!formData.endpoint) {
    ElMessage.warning('请输入连接地址')
    return
  }
  if (formData.id) {
    const index = integrations.value.findIndex(i => i.id === formData.id)
    if (index !== -1) {
      integrations.value[index] = { ...formData, lastSync: integrations.value[index].lastSync, syncCount: integrations.value[index].syncCount }
    }
  } else {
    formData.id = Date.now()
    formData.lastSync = '-'
    formData.syncCount = 0
    integrations.value.push({ ...formData })
  }
  ElMessage.success('保存成功')
  dialogVisible.value = false
}

const syncIntegration = (row) => {
  if (row.status !== '启用') {
    ElMessage.warning('请先启用集成')
    return
  }
  ElMessage.info(`正在同步 ${row.integrationName}...`)
  setTimeout(() => {
    row.lastSync = new Date().toLocaleString()
    row.syncCount++
    ElMessage.success(`${row.integrationName} 同步成功`)
  }, 1000)
}

const testConnection = () => {
  ElMessage.info('正在测试连接...')
  setTimeout(() => {
    ElMessage.success('所有连接测试通过')
  }, 1500)
}

const deleteIntegration = async (id) => {
  integrations.value = integrations.value.filter(item => item.id !== id)
  ElMessage.success('删除成功')
}
</script>

<style scoped>
.stats-row {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
}

.stat-card {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 16px;
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #ecf5ff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #409eff;
  font-size: 24px;
}

.stat-icon.success {
  background: #f0f9eb;
  color: #67c23a;
}

.stat-icon.warning {
  background: #fdf6ec;
  color: #e6a23c;
}

.stat-info {
  display: flex;
  flex-direction: column;
}

.stat-value {
  font-size: 28px;
  font-weight: bold;
  color: #303133;
}

.stat-label {
  font-size: 14px;
  color: #909399;
}
</style>